import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { NgxDatetimePickerComponent } from '../ngx-datetime-picker/ngx-datetime-picker.component';
import { ENVIRONMENTS, LTI_ENVIRONMENT_MODES } from '@go-modules/services/environment-vars/environments';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { AppDataService } from '@go-modules/services/app-data/app-data.service';
import { ActivityGradingComponent } from '../activity-grading/activity-grading.component';
import { ActivityAttachmentsComponent } from '../activity-attachments/activity-attachments.component';
import { ActivityEditAiMarkersSectionComponent } from '../ai-markers-section/activity-edit-ai-markers-section.component';
import { NgxActivityEditMarkerSectionComponent } from '../marker-section/ngx-activity-edit-marker-section.component';
import { ActivityTypeDropdownComponent } from '../activity-type-dropdown/activity-type-dropdown.component';
import { SourceMediaSettingComponent } from '../source-media-setting/source-media-setting.component';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { take } from 'rxjs';

@Component({
	selector: 'ngx-activity-edit',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		NgxDatetimePickerComponent,
		ActivityGradingComponent,
		ActivityAttachmentsComponent,
		ActivityEditAiMarkersSectionComponent,
		NgxActivityEditMarkerSectionComponent,
		ActivityTypeDropdownComponent,
		SourceMediaSettingComponent
	],
	templateUrl: './ngx-activity-edit.component.html',
	styleUrls: ['./ngx-activity-edit.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class NgxActivityEditComponent implements OnInit {
	@Input() public activity: ActivityModel;
	@Input() public hideName: boolean = false;
	@Input() public hideDates: boolean = false;
	@ViewChild(ActivityEditAiMarkersSectionComponent) private aiMarkersSection?: ActivityEditAiMarkersSectionComponent;
	@ViewChild(NgxActivityEditMarkerSectionComponent) private markerSection?: NgxActivityEditMarkerSectionComponent;
	public selectedTemplate: any;
	public isVideoShareEnabled: boolean | undefined;

	constructor (
		public activityFormService: ActivityFormService,
		private ltiAppDataService: AppDataService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private cdr: ChangeDetectorRef,
		private eventService: EventService
	) {}

	public ngOnInit (): void {
		const environment = EnvironmentVarsService.getInstance().get(EnvironmentVarsService.VAR.ENVIRONMENT);
		const isLtiEnvironment = environment?.name === ENVIRONMENTS.LTI &&
			environment?.mode === LTI_ENVIRONMENT_MODES.DEFAULT;

		if (isLtiEnvironment) {
			this.ltiAppDataService.appData$.pipe(take(1)).subscribe((data) => {
				this.hideDates = data.hideDates;
				this.cdr.markForCheck();
			});
		}
	}

	public shouldShowSourceMediaSetting (): boolean {
		return this.isViewable('source_media') || this.isViewable('has_self_select_source_media');
	}

	public handleTemplateSelect (template): void {
		this.selectedTemplate = template;
		if (this.activity) {
			if (typeof this.activity.setActivityTemplate === 'function') {
				this.activity.setActivityTemplate(template);
			} else {
				(this.activity as ActivityModel & { template?: any }).template = template;
			}
		}

		// if live session is incompatible with the template, set it to false
		if (this.selectedTemplate?.isNone?.('live_session_enabled')) {
			this.activityFormService.getControl('live_session_enabled')?.setValue(false);
		}

		if (this.selectedTemplate?.isNone?.('is_video_share_enabled')) {
			this.isVideoShareEnabled = false;
		}

		if (!this.selectedTemplate?.isActive?.('ai_prompts_enabled')) {
			this.handleAiMarkerSetRemove();
		}

		if (this.selectedTemplate?.isNone?.('tag_set_id')) {
			this.clearMarkerSelection();
		}

		if (this.selectedTemplate?.isWrite('source_media') && this.selectedTemplate.isActive('has_response_media')) {
			this.activityFormService.setControlValidators('source_type', [Validators.required]);
			this.activityFormService.clearControlValidators('source_media');
		} else if (this.selectedTemplate?.isWrite('source_media') && !this.selectedTemplate.isActive('has_response_media')) {
			this.activityFormService.setControlValidators('source_media', [Validators.required]);
			this.activityFormService.clearControlValidators('source_type');
		} else {
			this.activityFormService.clearControlValidators('source_type');
			this.activityFormService.clearControlValidators('source_media');
		}

		this.eventService.broadcast(
			EVENT_NAMES.ACTIVITY_EDIT_ACTIVITY_TYPE_CHANGE,
			{ has_response_media: !this.selectedTemplate?.isNone?.('has_response_media') }
		);
	}

	public resetAiSelections (): void {
		this.aiMarkersSection?.resetSelections();
	}

	public resetMarkerSelection (): void {
		this.markerSection?.resetSelection();
	}

	private isViewable (property: string): boolean {
		if (!this.selectedTemplate) {
			return false;
		}

		return !this.selectedTemplate.isNone?.(property) &&
			!this.selectedTemplate.isActive?.(property);
	}

	private handleAiMarkerSetRemove (): void {
		if (this.aiMarkersSection) {
			this.aiMarkersSection.removeAiMarkerSet();
			return;
		}

		const llmDefinitionsControl = this.activityFormService.getControl('llm_definitions');
		const languageControl = this.activityFormService.getControl('ai_transcription_language');
		const promptsControl = this.activityFormService.getControl('ai_prompts_enabled');

		llmDefinitionsControl?.setValue([]);
		languageControl?.setValue(null);
		promptsControl?.setValue(false);

		llmDefinitionsControl?.markAsDirty();
		languageControl?.markAsDirty();
		promptsControl?.markAsDirty();
		this.activityFormService.markFormAsDirty();
	}

	private clearMarkerSelection (): void {
		if (this.markerSection) {
			this.markerSection.clearSelection();
			return;
		}

		const control = this.activityFormService.getControl('tag_set_id');
		control?.setValue(null);
		control?.markAsDirty();
		this.activityFormService.markFormAsDirty();
	}
}
